import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Config } from '../config';
import { PusherService } from './pusher.service';
import { UserService } from './user.service';
import { AuthService } from './auth.service';

import 'cordova-plugin-purchase';

export type IapticConfig = CdvPurchase.IapticConfig;

export type RegisterProduct = {
  id: string;
  type: keyof typeof CdvPurchase.ProductType;
  platform?: CdvPurchase.Platform;
  group?: string;
};

export type Serialized<T> = {
  readonly [key in keyof T as T[key] extends (...args: never[]) => unknown
    ? never
    : key]: T[key] extends Array<infer R>
    ? ReadonlyArray<Serialized<R>>
    : T[key] extends ReadonlyArray<infer R>
      ? ReadonlyArray<Serialized<R>>
      : T[key] extends Date
        ? T[key]
        : T[key] extends object
          ? Serialized<T[key]>
          : T[key];
};

export type IError = Serialized<CdvPurchase.IError>;
export type Offer = Serialized<CdvPurchase.Offer>;
export type PricingPhase = Serialized<CdvPurchase.PricingPhase>;
export type Product = Serialized<CdvPurchase.Product>;
export type Receipt = Serialized<CdvPurchase.Receipt>;
export type Transaction = Serialized<CdvPurchase.Transaction>;
export type UnverifiedReceipt = Serialized<CdvPurchase.UnverifiedReceipt>;
export type VerifiedReceipt = Serialized<CdvPurchase.VerifiedReceipt>;

export enum PurchaseEventType {
  APPROVED = 'approved',
  ERROR = 'error',
  FINISHED = 'finished',
  UNVERIFIED = 'unverified',
  VERIFIED = 'verified',
}

export type PurchaseErrorEvent = {
  event: PurchaseEventType.ERROR;
  product: Product;
  error: IError;
};

export type PurchaseTransactionEvent = {
  event: PurchaseEventType.APPROVED | PurchaseEventType.FINISHED;
  product: Product;
  transaction: Transaction;
};

export type PurchaseUnverifiedReceiptEvent = {
  event: PurchaseEventType.UNVERIFIED;
  product: Product;
  unverifiedReceipt: UnverifiedReceipt;
};

export type PurchaseVerifiedReceiptEvent = {
  event: PurchaseEventType.VERIFIED;
  product: Product;
  verifiedReceipt: VerifiedReceipt;
};

export type PurchaseEvent =
  | PurchaseErrorEvent
  | PurchaseTransactionEvent
  | PurchaseUnverifiedReceiptEvent
  | PurchaseVerifiedReceiptEvent;

export type HasSubscription =
  | {
  has_subscription: false;
}
  | {
  has_subscription: true;
  product_id:
    | (typeof Config.inAppProductsConfig.products)[number]['id']
    | null;
  expires: number | null;
  status: 'Active' | 'Cancelled' | 'Paused' | 'Ended' | null;
};

@Injectable({ providedIn: 'root' })
export class InAppService {
  private userUuid: string;

  private configured = false;
  private initialized = false;
  private purchasing = false;

  private initializing?: Promise<void>;
  private restoring?: Promise<void>;
  private updating?: Promise<void>;

  private iaptic?: CdvPurchase.Iaptic;

  private readonly error$ = new Subject<Error | IError>();

  private readonly isSubscribed$ = new BehaviorSubject<HasSubscription>({
    has_subscription: false,
  });

  private readonly productUpdated$ = new Subject<Product>();
  private readonly productsUpdated$ = new Subject<Array<Product>>();

  private readonly purchaseApproved$ = new Subject<Transaction>();
  private readonly purchaseFinished$ = new Subject<Transaction>();
  private readonly purchasePending$ = new Subject<Transaction>();
  private readonly purchaseUnverified$ = new Subject<UnverifiedReceipt>();
  private readonly purchaseVerified$ = new Subject<VerifiedReceipt>();

  private readonly receiptsReady$ = new Subject<Receipt[]>();
  private readonly receiptUpdated$ = new Subject<Receipt>();
  private readonly receiptsVerified$ = new Subject<VerifiedReceipt[]>();

  constructor(
    private readonly http: HttpClient,
    private readonly platform: Platform,
    pusherService: PusherService,
    private readonly translate: TranslateService,
    userService: UserService,
    authService: AuthService
  ) {
    userService.updatedUser.subscribe((user) => {
      if (!user) {
        this.userUuid = undefined;
        this.isSubscribed$.next({ has_subscription: false });

        return;
      }

      if (user.is_premium) {
        this.isSubscribed$.next({
          has_subscription: true,
          product_id: user.subscription?.product_id ?? null,
          expires: user.subscription?.expires ?? null,
          status: user.subscription?.status ?? null,
        });
      } else {
        this.isSubscribed$.next({ has_subscription: false });
      }

      this.userUuid = user?.uuid;
    });

    userService.userData
      .pipe(
        distinctUntilChanged((lhs, rhs) =>
          lhs && rhs ? lhs.uuid === rhs.uuid : lhs === rhs
        ),
        debounceTime(300)
      )
      .subscribe(() => {
        this.refreshReceipts().catch((reason) => {
          console.warn(reason);
        });
      });

    pusherService.subscriptionChange
      .pipe(
        switchMap(() => authService.loggedIn),
        switchMap((loggedIn) => loggedIn ? userService.getUpdatedUser() : of(undefined)),
        catchError(() => of(undefined))
      )
      .subscribe();
  }

  get error(): Observable<Error | IError> {
    return this.error$.pipe(delay(0));
  }

  get isBusy(): boolean {
    return (
      this.isInitializing ||
      this.isPurchasing ||
      this.isRestoring ||
      this.isUpdating
    );
  }

  get isConfigured(): boolean {
    return this.configured;
  }

  get isInitialized(): boolean {
    return this.isConfigured && this.initialized;
  }

  get isInitializing(): boolean {
    return this.initializing !== undefined;
  }

  get isPurchasing(): boolean {
    return this.purchasing;
  }

  get isRestoring(): boolean {
    return this.restoring !== undefined;
  }

  get isSubscribed(): Observable<HasSubscription> {
    return this.isSubscribed$.asObservable();
  }

  get isSupported() {
    return (
      (this.platform.is('cordova') || this.platform.is('capacitor')) &&
      (this.platform.is('android') || this.platform.is('ios'))
    );
  }

  get isUpdating(): boolean {
    return this.updating !== undefined;
  }

  get products(): Array<Product> {
    return CdvPurchase.store.products.map((product) =>
      this.serializeProduct(product)
    );
  }

  get productUpdated(): Observable<Product> {
    return this.productUpdated$.pipe(delay(0));
  }

  get productsUpdated(): Observable<Array<Product>> {
    return this.productsUpdated$.pipe(debounceTime(300));
  }

  get purchaseApproved(): Observable<Transaction> {
    return this.purchaseApproved$.pipe(delay(0));
  }

  get purchaseFinished(): Observable<Transaction> {
    return this.purchaseFinished$.pipe(delay(0));
  }

  get purchasePending(): Observable<Transaction> {
    return this.purchasePending$.pipe(delay(0));
  }

  get purchaseUnverified(): Observable<UnverifiedReceipt> {
    return this.purchaseUnverified$.pipe(delay(0));
  }

  get purchaseVerified(): Observable<VerifiedReceipt> {
    return this.purchaseVerified$.pipe(delay(0));
  }

  get receiptsReady(): Observable<Receipt[]> {
    return this.receiptsReady$.pipe(delay(0));
  }

  get receiptUpdated(): Observable<Receipt> {
    return this.receiptUpdated$.pipe(delay(0));
  }

  get receiptsVerified(): Observable<VerifiedReceipt[]> {
    return this.receiptsVerified$.pipe(delay(0));
  }

  async configure(): Promise<void> {
    if (!this.isSupported) {
      throw new Error(this.translate.instant('IAP.UnsupportedPlatform'));
    }

    if (this.configured) {
      return;
    }

    this.configured = true;

    CdvPurchase.store.verbosity = environment.production
      ? CdvPurchase.LogLevel.WARNING
      : CdvPurchase.LogLevel.DEBUG;

    let validator: string | undefined;
    if (Config.inAppProductsConfig.iaptic) {
      this.iaptic = new CdvPurchase.Iaptic(Config.inAppProductsConfig.iaptic);

      validator = this.iaptic.validator;
    } else if (Config.inAppProductsConfig.validatorUrl) {
      validator = Config.inAppProductsConfig.validatorUrl;
    }

    CdvPurchase.store.validator = validator;
    CdvPurchase.store.applicationUsername = () => this.userUuid;

    CdvPurchase.store.error((error: CdvPurchase.IError) => {
      console.warn(error);

      this.error$.next(error);
    });

    const platform = this.platform.is('ios')
      ? CdvPurchase.Platform.APPLE_APPSTORE
      : CdvPurchase.Platform.GOOGLE_PLAY;

    CdvPurchase.store.register(
      Config.inAppProductsConfig.products.map((product) => ({
        platform,
        ...product,
        type: CdvPurchase.ProductType[product.type],
      }))
    );

    CdvPurchase.store
      .when()
      .approved((transaction) => {
        if (!environment.production) {
          console.log(`purchase approved`, transaction);
        }

        this.purchaseApproved$.next(transaction);

        transaction.products.forEach((product: { id: string }) => {
          this.updateProduct(CdvPurchase.store.get(product.id, platform));
        });

        transaction.verify().catch((reason) => {
          console.warn('Verify failed');
          console.warn(reason);
        });
      })
      .finished((transaction) => {
        if (!environment.production) {
          console.log(`purchase finished`, transaction);
        }

        this.purchaseFinished$.next(transaction);

        transaction.products.forEach((product: { id: string }) => {
          this.updateProduct(CdvPurchase.store.get(product.id, platform));
        });
      })
      .pending((transaction) => {
        if (!environment.production) {
          console.log(`purchase pending`, transaction);
        }

        this.purchasePending$.next(transaction);

        transaction.products.forEach((product: { id: string }) => {
          this.updateProduct(CdvPurchase.store.get(product.id, platform));
        });

        // TODO maybe required??
        // transaction.verify().catch((reason) => {
        //   console.warn('Verify failed');
        //   console.warn(reason);
        // });
      })
      .productUpdated((product) => {
        if (!environment.production) {
          console.log(`product updated: ${product.id}`, product);
        }

        this.updateProduct(product);
      })
      .receiptUpdated((receipt) => {
        if (!environment.production) {
          console.log(`receipt updated`, receipt);
        }

        this.receiptUpdated$.next(receipt);

        receipt.transactions.forEach((transaction) => {
          transaction.products.forEach((product: { id: string }) => {
            this.updateProduct(CdvPurchase.store.get(product.id, platform));
          });
        });
      })
      .receiptsReady(() => {
        if (!environment.production) {
          console.log(`receipts ready`);
        }

        const receipts = CdvPurchase.store.localReceipts;

        this.receiptsReady$.next(receipts);

        receipts.forEach((receipt) => {
          receipt.transactions.forEach((transaction) => {
            transaction.products.forEach((product: { id: string }) => {
              this.updateProduct(CdvPurchase.store.get(product.id, platform));
            });
          });
        });
      })
      .receiptsVerified(() => {
        if (!environment.production) {
          console.log(`receipts verified`);
        }

        const receipts = CdvPurchase.store.verifiedReceipts;

        this.receiptsVerified$.next(receipts);

        receipts.forEach((receipt) => {
          receipt.sourceReceipt.transactions.forEach((transaction) => {
            transaction.products.forEach((product: { id: string }) => {
              this.updateProduct(CdvPurchase.store.get(product.id, platform));
            });
          });
        });
      })
      .unverified((unverifiedReceipt) => {
        if (!environment.production) {
          console.log(`purchase unverified`, unverifiedReceipt);
        }

        this.purchaseUnverified$.next(unverifiedReceipt);

        unverifiedReceipt.receipt.transactions.forEach((transaction) => {
          transaction.products.forEach((product: { id: string }) => {
            this.updateProduct(CdvPurchase.store.get(product.id, platform));
          });
        });
      })
      .verified((verifiedReceipt) => {
        if (!environment.production) {
          console.log(
            `purchase verified: ${verifiedReceipt.id}`,
            verifiedReceipt
          );
        }

        this.purchaseVerified$.next(verifiedReceipt);

        const product = CdvPurchase.store.get(verifiedReceipt.id, platform);

        this.updateProduct(product);

        verifiedReceipt.finish().catch((reason) => {
          console.warn('Finish failed');
          console.warn(reason);
        });
      });
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!this.initializing) {
      this.initializing = (async () => {
        try {
          await this.platform.ready();

          await this.configure();

          const errors = await CdvPurchase.store.initialize(
            this.platform.is('ios')
              ? [
                {
                  platform: CdvPurchase.Platform.APPLE_APPSTORE,
                  options: {
                    discountEligibilityDeterminer:
                    this.iaptic?.appStoreDiscountEligibilityDeterminer,
                  },
                },
              ]
              : [
                {
                  platform: CdvPurchase.Platform.GOOGLE_PLAY,
                },
              ]
          );


          if (errors?.length) {
            console.warn('Error during initialization', errors);

            errors.forEach((error) => {
              this.error$.next(error);
            });
          }

          await this.storeReady();

          this.initialized = true;
        } catch (error) {
          console.error('initialize error', error);

          throw error;
        } finally {
          this.initializing = undefined;
        }
      })();
    }

    return this.initializing;
  }

  async refreshReceipts(): Promise<void> {
    CdvPurchase.store.verifiedReceipts.splice(
      0,
      CdvPurchase.store.verifiedReceipts.length
    );

    if (this.userUuid) {
      // Verify the local receipts so the user owning the purchase/subscription can be determined correctly
      await Promise.all(
        CdvPurchase.store.localReceipts.map((receipt) => receipt.verify())
      );
    }
  }

  async restorePurchases(): Promise<void> {
    if (!this.restoring) {
      this.restoring = (async () => {
        if (this.purchasing) {
          this.restoring = undefined;

          throw new Error(this.translate.instant('IAP.PurchaseInProgress'));
        }

        try {
          await this.initialize();

          if (this.updating) {
            await this.updating;
          }

          await CdvPurchase.store.restorePurchases();
        } catch (error) {
          console.error('restorePurchases error', error);

          throw error;
        } finally {
          this.restoring = undefined;
        }
      })();
    }

    return this.restoring;
  }

  async updateProducts() {
    if (!this.updating) {
      this.updating = (async () => {
        if (this.purchasing) {
          this.updating = undefined;

          throw new Error(this.translate.instant('IAP.PurchaseInProgress'));
        }

        try {
          await this.initialize();

          if (this.restoring) {
            await this.restoring;
          }

          await CdvPurchase.store.update();
        } catch (error) {
          console.error('updateProducts error', error);

          throw error;
        } finally {
          this.updating = undefined;
        }
      })();
    }

    return this.updating;
  }

  handleSubscription(
    productId: string,
    offerId?: string,
    additionalData: CdvPurchase.AdditionalData = {}
  ): Observable<PurchaseEvent> {
    return new Observable<PurchaseEvent>((subscriber) => {
      const order = async () => {
        console.log('Purchasing for: ', this.userUuid);

        await this.initialize();

        if (this.updating) {
          await this.updating;
        }

        if (this.restoring) {
          await this.restoring;
        }

        if (this.purchasing) {
          throw new Error(this.translate.instant('IAP.PurchaseInProgress'));
        }

        this.purchasing = true;

        const product = CdvPurchase.store.get(productId);
        if (!product) {
          this.purchasing = false;

          throw new Error(this.translate.instant('IAP.InvalidProduct'));
        }

        const offer = product.getOffer(offerId);
        if (!offer) {
          this.purchasing = false;

          throw new Error(this.translate.instant('IAP.InvalidOffer'));
        }

        if (!product.canPurchase) {
          this.purchasing = false;

          throw new Error(this.translate.instant('IAP.CannotBePurchased'));
        }

        let oldPurchaseToken: string | undefined;
        if (this.platform.is('android')) {
          if (product.type === CdvPurchase.ProductType.PAID_SUBSCRIPTION) {
            if (!additionalData.googlePlay) {
              additionalData.googlePlay = {
                replacementMode:
                CdvPurchase.GooglePlay.ReplacementMode.WITH_TIME_PRORATION,
              };
            } else if (!additionalData.googlePlay.replacementMode) {
              additionalData.googlePlay.replacementMode =
                CdvPurchase.GooglePlay.ReplacementMode.WITH_TIME_PRORATION;
            }
          }

          if (product.group) {
            const adapter = CdvPurchase.store.getAdapter(
              CdvPurchase.Platform.GOOGLE_PLAY
            ) as CdvPurchase.GooglePlay.Adapter;

            oldPurchaseToken = adapter.findOldPurchaseToken(
              product.id,
              product.group
            );
          }
        }

        const cleanup = () => {
          CdvPurchase.store.off(onApproved);
          CdvPurchase.store.off(onError);
          CdvPurchase.store.off(onFinished);
          CdvPurchase.store.off(onUnverified);
          CdvPurchase.store.off(onVerified);

          this.purchasing = false;

          subscriber.complete();
        };

        const sendEvent = (event: PurchaseEvent) => {
          if (!environment.production) {
            console.log('purchaseEvent', event);
          }

          if (subscriber.closed) {
            return;
          }

          subscriber.next(event);

          if (
            event.event === PurchaseEventType.ERROR ||
            event.event === PurchaseEventType.FINISHED ||
            event.event === PurchaseEventType.UNVERIFIED
          ) {
            cleanup();
          }
        };

        const onApproved = (transaction: CdvPurchase.Transaction) => {
          sendEvent({
            event: PurchaseEventType.APPROVED,
            product: this.serializeProduct(product),
            transaction,
          });
        };

        const onError = (error: CdvPurchase.IError) => {
          sendEvent({
            event: PurchaseEventType.ERROR,
            product: this.serializeProduct(product),
            error,
          });
        };

        const onFinished = (transaction: CdvPurchase.Transaction) => {
          sendEvent({
            event: PurchaseEventType.FINISHED,
            product: this.serializeProduct(product),
            transaction,
          });

          this.fixReceipts(product, oldPurchaseToken);
        };

        const onUnverified = (
          unverifiedReceipt: CdvPurchase.UnverifiedReceipt
        ) => {
          sendEvent({
            event: PurchaseEventType.UNVERIFIED,
            product: this.serializeProduct(product),
            unverifiedReceipt,
          });
        };

        const onVerified = (verifiedReceipt: CdvPurchase.VerifiedReceipt) => {
          sendEvent({
            event: PurchaseEventType.VERIFIED,
            product: this.serializeProduct(product),
            verifiedReceipt,
          });
        };

        CdvPurchase.store.error(onError);

        CdvPurchase.store
          .when()
          .approved(onApproved)
          .finished(onFinished)
          .unverified(onUnverified)
          .verified(onVerified);

        const error = await offer.order(additionalData);
        if (error) {
          sendEvent({
            event: PurchaseEventType.ERROR,
            product: this.serializeProduct(product),
            error,
          });
        }
      };

      order().catch((error) => subscriber.error(error));
    });
  }

  isOwned(productId: string) {
    const platform = this.platform.is('ios')
      ? CdvPurchase.Platform.APPLE_APPSTORE
      : CdvPurchase.Platform.GOOGLE_PLAY;

    return CdvPurchase.store.owned({ id: productId, platform });
  }

  private serializeProduct(product: CdvPurchase.Product): Product {
    return {
      className: product.className,
      platform: product.platform,
      type: product.type,
      id: product.id,
      offers: product.offers.map(
        (offer): Offer => ({
          id: offer.id,
          productId: offer.productId,
          productType: offer.productType,
          productGroup: offer.productGroup,
          platform: offer.platform,
          pricingPhases: offer.pricingPhases.map(
            (phase): PricingPhase => ({
              price: phase.price,
              priceMicros: phase.priceMicros,
              currency: phase.currency,
              billingPeriod: phase.billingPeriod,
              billingCycles: phase.billingCycles,
              recurrenceMode: phase.recurrenceMode,
              paymentMode: phase.paymentMode,
            })
          ),
          canPurchase: offer.canPurchase,
        })
      ),
      title: product.title,
      description: product.description,
      group: product.group,
      canPurchase: product.canPurchase,
      owned: product.owned,
      pricing: product.pricing,
    };
  }

  private async storeReady() {
    const receiptsLoaded = new Promise<void>((resolve) => {
      const loaded = () => {
        CdvPurchase.store.off(loaded);

        resolve();
      };

      if (CdvPurchase.store.validator) {
        CdvPurchase.store.when().receiptsVerified(loaded);
      } else {
        CdvPurchase.store.when().receiptsReady(loaded);
      }
    });

    await new Promise<void>((resolve) => {
      CdvPurchase.store.ready(resolve);
    });

    await receiptsLoaded;
  }

  private updateProduct(product: CdvPurchase.Product | undefined): void {
    if (!product) {
      return;
    }

    this.productUpdated$.next(this.serializeProduct(product));
    this.productsUpdated$.next(this.products);
  }

  /**
   * TODO hack to fix the receipts for Android -> plugin does not forget the receipt when switching subscriptions
   */
  private fixReceipts(product: CdvPurchase.Product, oldPurchaseToken?: string) {
    if (
      !this.platform.is('android') ||
      product.type !== CdvPurchase.ProductType.PAID_SUBSCRIPTION ||
      !oldPurchaseToken
    ) {
      return;
    }

    const adapter = CdvPurchase.store.getAdapter(
      CdvPurchase.Platform.GOOGLE_PLAY
    ) as CdvPurchase.GooglePlay.Adapter;
    const oldReceipt = adapter.receipts.find(
      (receipt) => receipt.purchaseToken === oldPurchaseToken
    );

    if (oldReceipt) {
      const receiptIdx = adapter.receipts.indexOf(oldReceipt);

      if (receiptIdx > -1) {
        adapter.receipts.splice(receiptIdx, 1);
      }

      const verifiedReceiptIdx = CdvPurchase.store.verifiedReceipts.findIndex(
        (receipt) =>
          receipt.platform === CdvPurchase.Platform.GOOGLE_PLAY &&
          (receipt.sourceReceipt as CdvPurchase.GooglePlay.Receipt)
            .purchaseToken === oldPurchaseToken
      );
      if (verifiedReceiptIdx > -1) {
        CdvPurchase.store.verifiedReceipts.splice(verifiedReceiptIdx, 1);
      }

      const productIds = oldReceipt.transactions
        .reduce<string[]>(
          (acc, curr) => acc.concat(curr.products.map((p) => p.id)),
          []
        )
        .filter((curr, idx, src) => src.indexOf(curr) === idx);

      productIds.forEach((id) => {
        this.updateProduct(CdvPurchase.store.get(id));
      });
    }
  }
}
